import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { ICommsStudentEntry } from 'core/hooks';
import { CommunicatorCtx } from 'core/utils/communicatorDeepLink';

import * as S from './Communicator.styled';

interface IStudentsColumnProps {
  students: ICommsStudentEntry[];
  channelsUnread: number;
  channelsUnanswered: boolean;
  loading: boolean;
  selectedStudentId: string;
  ctx: CommunicatorCtx;
  onSelectStudent: (studentId: string) => void;
  onSelectChannels: () => void;
}

/**
 * Middle pane: the pinned "group channels" item + the group's students, each
 * with an unread bubble and an amber "waiting for a reply" dot. Fed entirely
 * by `useGroupCommsIndex` (owned by the page so the main pane shares the same
 * sweep).
 */
export const StudentsColumn = memo(
  ({
    students,
    channelsUnread,
    channelsUnanswered,
    loading,
    selectedStudentId,
    ctx,
    onSelectStudent,
    onSelectChannels,
  }: IStudentsColumnProps) => {
    const { t } = useTranslation();

    return (
      <S.Pane>
        <S.PaneHeader>{t('pages.communicator.studentsLabel')}</S.PaneHeader>
        <S.PaneList>
          <S.PinnedRow
            type="button"
            active={ctx === 'channels'}
            onClick={onSelectChannels}
          >
            <S.RowMain>
              <b># {t('pages.communicator.channelsItem')}</b>
              <span>{t('pages.communicator.channelsSub')}</span>
            </S.RowMain>
            {channelsUnanswered && <S.UnansweredDot />}
            {channelsUnread > 0 && (
              <S.UnreadBubble>{channelsUnread}</S.UnreadBubble>
            )}
          </S.PinnedRow>

          {students.map((student) => (
            <S.Row
              key={student.studentId}
              type="button"
              active={student.studentId === selectedStudentId}
              onClick={() => onSelectStudent(student.studentId)}
            >
              <S.RowMain>
                <b>
                  {student.lastName} {student.firstName}
                </b>
                <span>
                  {student.inRoster
                    ? student.email
                    : t('pages.communicator.notInGroup')}
                </span>
              </S.RowMain>
              {student.unanswered && <S.UnansweredDot />}
              {student.unread > 0 && (
                <S.UnreadBubble>{student.unread}</S.UnreadBubble>
              )}
            </S.Row>
          ))}

          {!loading && !students.length && (
            <S.Muted>{t('pages.communicator.studentsEmpty')}</S.Muted>
          )}
        </S.PaneList>
      </S.Pane>
    );
  },
);

StudentsColumn.displayName = 'StudentsColumn';
